import React, { useEffect, useMemo } from "react";
import { useFieldArray } from "react-hook-form";
import { RHFTextField } from '../../../common/hook-form';
import { Box, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RHFSelectField from "../../../common/hook-form/RHFSelectField";
import { factList, ndrReasonList, ofdCountList, paymentModeList } from "../../../helpers/UtilityHelper";
import useGetCourier from "../../dashboard/performance/useGetCourier";
import _ from "lodash";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { SUBTLE, BORDER, PRIMARY_BLUE, withOpacity } from '../../../styles/foundation/colors';

// Plain-language labels for the backend "fact" keys — kept in sync with factList values.
const FACT_LABELS = {
    ofdCount: 'Failed delivery attempts',
    ndrReason: 'NDR reason',
    paymentMode: 'Payment mode',
    courierParentName: 'Courier partner',
    daysSinceFirstAttempt: 'Days since first attempt',
};
const friendlyFacts = factList.map((f) => ({ value: f.value, label: FACT_LABELS[f.value] || f.label }));

const addBtnSx = {
    display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none',
    border: `1px dashed ${withOpacity(PRIMARY_BLUE, 0.5)}`, borderRadius: '9px', px: 1.5, py: 1,
    fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: PRIMARY_BLUE, bgcolor: 'transparent',
    '&:hover': { bgcolor: withOpacity(PRIMARY_BLUE, 0.06), borderColor: PRIMARY_BLUE },
};

const FormField = ({ watch, control, reset }) => {
    const { fields, append, remove } = useFieldArray({ control, name: "list" });

    const list = watch("list") || [];
    const selectedFacts = list.map((x) => x?.fact).filter(Boolean);
    const availableOptions = useMemo(
        () => friendlyFacts.filter((f) => !selectedFacts.includes(f.value)),
        [selectedFacts]
    );

    return (
        <Box>
            {fields.map((field, index) => {
                const currentFact = list[index]?.fact || field.fact;
                const selectedFact = { value: currentFact, label: FACT_LABELS[currentFact] || '' };
                return (
                    <React.Fragment key={field.id}>
                        {index > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                                <Box sx={{ flex: 1, borderTop: `1px dashed ${BORDER}` }} />
                                <Typography sx={{ fontFamily: FONT, fontSize: 10, fontWeight: WEIGHT.extra, color: SUBTLE, letterSpacing: '0.08em' }}>AND</Typography>
                                <Box sx={{ flex: 1, borderTop: `1px dashed ${BORDER}` }} />
                            </Box>
                        )}
                        <Row
                            index={index}
                            watch={watch}
                            fields={fields.length}
                            remove={remove}
                            reset={reset}
                            availableOptions={availableOptions}
                            selectedFact={selectedFact}
                        />
                    </React.Fragment>
                );
            })}

            {availableOptions.length > 0 && (
                <Box sx={addBtnSx} onClick={() => append({ fact: '', fieldValue: [] })}>
                    <AddIcon sx={{ fontSize: 15 }} /> Add another condition
                </Box>
            )}
        </Box>
    );
};

export default FormField;

const Row = ({ index, watch, fields, remove, reset, selectedFact, availableOptions }) => {
    const { data: allCouriers } = useGetCourier();

    const couriers = useMemo(() => {
        return allCouriers && _.uniqBy(
            allCouriers.filter((val) => val.supported_shipment === "B2C"),
            'parent'
        ).map((val) => ({ label: val.parent, value: val.parent }));
    }, [allCouriers]);

    const valueOptionsFor = (factValue) => ({
        ofdCount: ofdCountList,
        ndrReason: ndrReasonList,
        paymentMode: paymentModeList,
        courierParentName: couriers,
    }[factValue] || []);

    const fact = watch(`list.${index}.fact`);
    const valueOptions = useMemo(() => valueOptionsFor(fact), [fact, couriers]);

    // Clear the value when the condition (fact) changes, so stale values don't submit.
    useEffect(() => { reset(`list.${index}.fieldValue`); }, [fact]);

    const factOptions = selectedFact.value ? [...availableOptions, selectedFact] : availableOptions;

    return (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
            <Box sx={{ flex: '1 1 220px', minWidth: 190 }}>
                <RHFSelectField
                    variant="filled"
                    name={`list.${index}.fact`}
                    label="Condition"
                    width="100%"
                    size="small"
                    options={factOptions}
                />
            </Box>
            <Box sx={{ flex: '0 0 auto', minWidth: 96 }}>
                {/* Operator is always "is any of" — shown for clarity, not editable (backend infers IN). */}
                <RHFSelectField
                    variant="filled"
                    name={`list.${index}.__operator`}
                    label="Is"
                    width="100%"
                    size="small"
                    disabled
                    options={[{ label: 'is any of', value: 'in' }]}
                />
            </Box>
            <Box sx={{ flex: '1 1 220px', minWidth: 190 }}>
                {fact === "daysSinceFirstAttempt" ? (
                    <RHFTextField
                        variant="filled"
                        name={`list.${index}.firstAttempt`}
                        label="Value (number of days)"
                        width="100%"
                        size="small"
                        type="number"
                    />
                ) : (
                    <RHFSelectField
                        variant="filled"
                        name={`list.${index}.fieldValue`}
                        label="Value (pick one or more)"
                        width="100%"
                        size="small"
                        multiSelect={true}
                        options={valueOptions}
                        noOptionsText="Pick a condition first"
                    />
                )}
            </Box>
            {fields > 1 && (
                <Box
                    onClick={() => remove(index)}
                    title="Remove condition"
                    sx={{
                        width: 40, height: 40, flexShrink: 0, borderRadius: '9px',
                        border: `1px solid ${BORDER}`, color: SUBTLE, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '&:hover': { borderColor: '#e5484d', color: '#e5484d', bgcolor: withOpacity('#e5484d', 0.08) },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 17 }} />
                </Box>
            )}
        </Box>
    );
};
